import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook: centralises admin data-loading + data-ops action runner.
 * Returns everything AdminLibrary needs to render + mutate stats/library/scans/users.
 *
 * - `loadAll()` hits stats / library / scans / users in parallel.
 * - `runAction(label, path)` drives seed-data / mailchimp-sync buttons.
 * - `fetchJSON(url)` exposed for callers that need one-off reads (e.g. barcode detail).
 * - 401 → /login redirect. 403 → `unauthorized=true`.
 */
export default function useAdminData() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  const [stats, setStats] = useState(null);
  const [library, setLibrary] = useState({ total: 0, items: [] });
  const [scans, setScans] = useState({ total: 0, items: [] });
  const [users, setUsers] = useState({ total: 0, users: [] });

  const [search, setSearch] = useState('');
  const [actionRunning, setActionRunning] = useState(null);
  const [actionResult, setActionResult] = useState(null);

  const fetchJSON = useCallback(
    async (url) => {
      const res = await fetch(url, { credentials: 'include' });
      if (res.status === 401) {
        navigate('/login');
        return null;
      }
      if (res.status === 403) {
        setUnauthorized(true);
        return null;
      }
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
    [navigate]
  );

  const loadAll = useCallback(async () => {
    try {
      const [s, l, sc, u] = await Promise.all([
        fetchJSON(`${API}/api/admin/stats`),
        fetchJSON(
          `${API}/api/admin/library?limit=200${
            search ? `&search=${encodeURIComponent(search)}` : ''
          }`
        ),
        fetchJSON(`${API}/api/admin/scans?limit=200`),
        fetchJSON(`${API}/api/admin/users`),
      ]);
      if (s) setStats(s);
      if (l) setLibrary(l);
      if (sc) setScans(sc);
      if (u) setUsers(u);
    } catch (err) {
      console.error('[Admin] Load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchJSON, search]);

  const runAction = useCallback(
    async (label, path) => {
      if (actionRunning) return;
      if (!window.confirm(`${label}?\n\nThis will hit ${path}. Safe to run multiple times.`)) return;
      setActionRunning(label);
      setActionResult(null);
      try {
        const res = await fetch(`${API}${path}`, { method: 'POST', credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || `${res.status}`);
        setActionResult({ label, ok: true, data });
        await loadAll();
      } catch (err) {
        setActionResult({ label, ok: false, error: String(err.message || err) });
      } finally {
        setActionRunning(null);
      }
    },
    [actionRunning, loadAll]
  );

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loading,
    unauthorized,
    stats,
    library,
    scans,
    users,
    search,
    setSearch,
    actionRunning,
    actionResult,
    fetchJSON,
    loadAll,
    runAction,
  };
}
